// Theme variables come from themes.ts via the Vite plugin; Tailwind can't resolve virtual
// modules in CSS @import, so they're imported here.
import 'virtual:themes.css';
import './styles.css';
import './app/i18n';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { MotionConfig } from 'motion/react';
import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { router, shouldRetry } from './app/router';
import { accessToken, configureCognito } from './auth/cognito';
import { SessionProvider } from './auth/session';
import { Beacon } from './components/feedback/Beacon';
import { loadConfig } from './config';
import { configureApi } from './lib/api';
import { ThemeProvider } from './theme/ThemeProvider';

// Boot: show Beacon while the runtime config loads, wire Cognito and the API client, then
// hand over to the router. A minimum Beacon time keeps a fast load from flickering.

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: shouldRetry, refetchOnWindowFocus: false } },
});

function Boot() {
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');

  useEffect(() => {
    const minimum = new Promise((r) => setTimeout(r, 900));
    loadConfig()
      .then(async (config) => {
        configureCognito(config);
        configureApi({ baseUrl: config.apiUrl, getToken: accessToken });
        await minimum;
        setState('ready');
      })
      .catch(() => setState('failed'));
  }, []);

  if (state !== 'ready') return <Beacon failed={state === 'failed'} />;
  return (
    <SessionProvider>
      <RouterProvider router={router} />
    </SessionProvider>
  );
}

const root = document.getElementById('root');
if (!root) throw new Error('#root missing');
createRoot(root).render(
  <StrictMode>
    <MotionConfig reducedMotion="user">
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <Boot />
        </ThemeProvider>
      </QueryClientProvider>
    </MotionConfig>
  </StrictMode>,
);
